// MuiCalendar.jsx
import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import Day from '../Day';
import { getDayColors as getColorsOriginal } from './utils';
import { IconButton } from '@mui/material';
import { ArrowBackIosNew, ArrowForwardIos } from '@mui/icons-material';
import { useLocale } from 'hooks';
import { format } from 'date-fns';

const today = new Date();

const getDayColors = (day, selectedDate = today) => {
  if (!day) return {};
  return getColorsOriginal(day, selectedDate, today);
};

function MuiCalendar({
  date = today,
  disabled = false,
  disableFuture = false,
  maxDate,
  minDate,
  onClick,
  onDayClick,
  disablePointer = false,
  disableMouseControls = false,
  // format = 'dd-MM-yyyy',
  getDayColors: getColors = getDayColors,
  hideHeaderDropdown = false,
  disablePrevMonth = false,
  disableNextMonth = false,
  ...rest
}) {
  const locale = useLocale();

  const shouldDisableDate = useCallback(
    day => {
      if (!day) return true;
      return (
        disabled ||
        (disableFuture && day > today) ||
        (maxDate && day > maxDate) ||
        (minDate && day < minDate) ||
        day.getMonth() !== date.getMonth()
      );
    },
    [disabled, disableFuture, date, maxDate, minDate]
  );

  const handleChange = useCallback(
    day => {
      if (!day) return;
      onClick?.(day);
    },
    [onClick]
  );

  const handleSetLastTarget = useCallback(
    (event, day) => {
      if (!day) return;
      const anchorEl =
        event?.currentTarget ||
        event?.target?.closest?.('button,[role="button"],[role="gridcell"],td,div') ||
        null;
      onDayClick?.(day, anchorEl);
    },
    [onDayClick]
  );

  function CustomCalendarHeader({ currentMonth, onMonthChange }) {
    const today = new Date();

    // normalize to first of month (ignore day)
    const startOfTodayMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const startOfCurrentMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);

    const handlePrev = () => {
      const prevMonth = new Date(currentMonth);
      prevMonth.setMonth(prevMonth.getMonth() - 1);
      onMonthChange(prevMonth);
    };

    const handleNext = () => {
      const nextMonth = new Date(currentMonth);
      nextMonth.setMonth(nextMonth.getMonth() + 1);
      onMonthChange(nextMonth);
    };

    const disableNext =
      startOfCurrentMonth.getFullYear() === startOfTodayMonth.getFullYear() &&
      startOfCurrentMonth.getMonth() === startOfTodayMonth.getMonth();

    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          margin: '12px',
          alignItems: 'center'
        }}
      >
        {/* Left Arrow */}
        <IconButton size="small" onClick={handlePrev} disabled={disablePrevMonth} sx={{ padding: '12px' }}>
          <ArrowBackIosNew sx={{ fontSize: '1rem' }} />
        </IconButton>

        {/* Month + Year */}
        <div style={{ fontWeight: 500 }}>
          {currentMonth.toLocaleString('de-DE', {
            month: 'long',
            year: 'numeric'
          })}
        </div>

        {/* Right Arrow */}
        <IconButton
          size="small"
          onClick={handleNext}
          disabled={disableNext || disableNextMonth}
          sx={{ padding: '12px' }}
        >
          <ArrowForwardIos sx={{ fontSize: '1rem' }} />
        </IconButton>
      </div>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={locale}>
      <DateCalendar
        value={date}
        disableFuture={disabled || disableFuture}
        disablePast={disabled}
        maxDate={maxDate}
        minDate={minDate}
        shouldDisableDate={shouldDisableDate}
        dayOfWeekFormatter={(day, date) => {
          return format(date, 'EEEEEE', { locale });
        }}
        slots={{
          day: dayProps => {
            const dayValue = dayProps?.day;
            if (!dayValue) return null;

            // Strip all internal/non-DOM props
            const {
              disableHighlightToday,
              isAnimating,
              onDaySelect,
              today,
              outsideCurrentMonth,
              isFirstVisibleCell,
              isLastVisibleCell,
              ...safeDayProps
            } = dayProps || {};

            return (
              <Day
                {...safeDayProps}
                disabled={shouldDisableDate(dayValue)}
                {...getColors(dayValue, date)}
                onClick={event => handleSetLastTarget(event, dayValue)}
                disablePointer={disablePointer}
              >
                {dayValue.getDate()}
              </Day>
            );
          },
          calendarHeader: hideHeaderDropdown ? CustomCalendarHeader : undefined
        }}
        onChange={handleChange}
        disableHighlightToday={disableMouseControls}
        {...rest}
      />
    </LocalizationProvider>
  );
}

MuiCalendar.propTypes = {
  date: PropTypes.instanceOf(Date),
  disabled: PropTypes.bool,
  disableFuture: PropTypes.bool,
  format: PropTypes.string,
  getDayColors: PropTypes.func,
  maxDate: PropTypes.instanceOf(Date),
  minDate: PropTypes.instanceOf(Date),
  onClick: PropTypes.func,
  onDayClick: PropTypes.func,
  disablePointer: PropTypes.bool,
  disableMouseControls: PropTypes.bool,
  hideHeaderDropdown: PropTypes.bool
};

export default MuiCalendar;
